import {
  Box,
  Flex,
  Text,
  Center,
  useColorModeValue,
  useBreakpointValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React from 'react';

import type { Block } from 'types/api/block';

import { route } from 'nextjs-routes';

import config from 'configs/app';
import getBlockTotalReward from 'lib/block/getBlockTotalReward';
import Skeleton from 'ui/shared/chakra/Skeleton';
import LinkInternal from 'ui/shared/links/LinkInternal';
import TimeAgoWithTooltip from 'ui/shared/TimeAgoWithTooltip';

type Props = {
  block: Block;
  isLoading?: boolean;
};

const LatestBlocksItem = ({ block, isLoading }: Props) => {
  const totalReward = getBlockTotalReward(block);
  const hashPrefixLen = useBreakpointValue({ base: 6, md: 10 });
  const hashSuffixLen = useBreakpointValue({ base: 6, md: 9 });
  const borderColor = useColorModeValue('#e9ecef', 'gray.600');
  const iconBg = useColorModeValue('#f8f9fa', 'gray.700');
  const iconStroke = useColorModeValue('#77838f', 'gray.400');
  const mutedColor = useColorModeValue('#6c757d', 'gray.400');
  const labelColor = useColorModeValue('#000', 'gray.200');
  const pillBorder = useColorModeValue('#00000026', 'gray.500');
  const pillColor = useColorModeValue('#000', 'gray.200');

  const minerDisplay = block.miner.name || `${ block.miner.hash.slice(0, 6) }...${ block.miner.hash.slice(-4) }`;

  return (
    <Flex
      as={ motion.div }
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ display: 'none' }}
      transitionDuration="normal"
      transitionTimingFunction="linear"
      alignItems="center"
      py={ 4 }
      px={{ base: 4, md: 5 }}
      borderBottom="1px solid"
      borderBottomColor={ borderColor }
      _last={{ borderBottom: 'none' }}
    >
      { /* Mobile: vertical stack (BscScan style) */ }
      <Box display={{ base: 'block', md: 'none' }} w="100%">
        <Skeleton isLoaded={ !isLoading }>
          <Text as="span" fontSize="14px" color={ labelColor }>Block </Text>
          <LinkInternal
            href={ route({ pathname: '/block/[height_or_hash]', query: { height_or_hash: String(block.height) } }) }
            fontSize="14px"
            fontWeight={ 500 }
            color="#066a9c"
            _hover={{ color: '#066a9c' }}
          >
            { block.height }
          </LinkInternal>
          <Box as="span" fontSize="13px" color={ mutedColor } ml={ 2 } display="inline-block">
            <TimeAgoWithTooltip
              timestamp={ block.timestamp }
              enableIncrement={ !isLoading }
              isLoading={ isLoading }
            />
          </Box>
        </Skeleton>
        { !config.features.rollup.isEnabled && !config.UI.views.block.hiddenFields?.miner && (
          <Skeleton isLoaded={ !isLoading } mt={ 1.5 }>
            <Text fontSize="13px" color={ labelColor }>
              Validated By Validator :{' '}
              <LinkInternal
                href={ route({ pathname: '/address/[hash]', query: { hash: block.miner.hash } }) }
                fontSize="13px"
                fontWeight={ 500 }
                color="#0784c3"
                _hover={{ color: '#066a9c' }}
              >
                { minerDisplay }
              </LinkInternal>
            </Text>
          </Skeleton>
        ) }
        <Skeleton isLoaded={ !isLoading } mt={ 1.5 }>
          <Flex alignItems="center" gap={ 2 } flexWrap="wrap">
            <LinkInternal
              href={ route({ pathname: '/block/[height_or_hash]', query: { height_or_hash: String(block.height) } }) }
              fontSize="13px"
              fontWeight={ 500 }
              color="#066a9c"
              _hover={{ color: '#066a9c' }}
            >
              { block.transaction_count } txns
            </LinkInternal>
            <Text as="span" fontSize="13px" color={ labelColor }>in 12 secs</Text>
            { !config.features.rollup.isEnabled && !config.UI.views.block.hiddenFields?.total_reward && (
              <Box
                borderRadius="6px"
                px={ 2.5 }
                py={ 1 }
                fontSize="12px"
                fontWeight={ 500 }
                color={ pillColor }
                whiteSpace="nowrap"
                bg={ iconBg }
              >
                { totalReward.dp(5).toFixed() } { config.chain.currency.symbol }
              </Box>
            ) }
          </Flex>
        </Skeleton>
      </Box>

      { /* Desktop: horizontal row (existing Etherscan style) */ }
      <Flex display={{ base: 'none', md: 'flex' }} alignItems="center" w="100%">
        <Center
          w="52px"
          h="52px"
          bg={ iconBg }
          borderRadius="10px"
          flexShrink={ 0 }
          mr={ 4 }
        >
          <Skeleton isLoaded={ !isLoading }>
            <Box as="svg" viewBox="0 0 24 24" fill="none" stroke={ iconStroke } strokeWidth="1" w="28px" h="28px">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
              <line x1="12" y1="22.08" x2="12" y2="12"/>
            </Box>
          </Skeleton>
        </Center>

        <Box minW="110px" mr={ 4 }>
          <Skeleton isLoaded={ !isLoading }>
            <LinkInternal
              href={ route({ pathname: '/block/[height_or_hash]', query: { height_or_hash: String(block.height) } }) }
              fontSize="15px"
              fontWeight={ 500 }
              color="#066a9c"
              _hover={{ color: '#066a9c' }}
            >
              { block.height }
            </LinkInternal>
          </Skeleton>
          <TimeAgoWithTooltip
            timestamp={ block.timestamp }
            enableIncrement={ !isLoading }
            isLoading={ isLoading }
            color={ mutedColor }
            fontSize="13px"
            display="block"
            mt={ 1 }
          />
        </Box>

        <Box flex={ 1 } minW={ 0 }>
          { !config.features.rollup.isEnabled && !config.UI.views.block.hiddenFields?.miner && (
            <Flex alignItems="center" gap={ 1.5 } fontSize="14px" mb={ 1 }>
              <Text color={ labelColor } flexShrink={ 0 }>Miner</Text>
              <Skeleton isLoaded={ !isLoading }>
                <LinkInternal
                  href={ route({ pathname: '/address/[hash]', query: { hash: block.miner.hash } }) }
                  fontSize="14px"
                  fontWeight={ 500 }
                  color="#0784c3"
                  _hover={{ color: '#066a9c' }}
                >
                  { block.miner.hash.slice(0, hashPrefixLen ?? 10) }...{ block.miner.hash.slice(-(hashSuffixLen ?? 9)) }
                </LinkInternal>
              </Skeleton>
            </Flex>
          ) }
          <Skeleton isLoaded={ !isLoading }>
            <Text fontSize="14px">
              <LinkInternal
                href={ route({ pathname: '/block/[height_or_hash]', query: { height_or_hash: String(block.height) } }) }
                color="#066A9C"
                fontWeight={ 400 }
                fontSize="14.5px"
                _hover={{ color: '#066a9c' }}
              >
                { block.transaction_count } txns
              </LinkInternal>
              <Text as="span" color={ iconStroke }> in 12 secs</Text>
            </Text>
          </Skeleton>
        </Box>

        { !config.features.rollup.isEnabled && !config.UI.views.block.hiddenFields?.total_reward && (
          <Skeleton isLoaded={ !isLoading }>
            <Box
              border="1px solid"
              borderColor={ pillBorder }
              borderRadius="6px"
              px={ 3 }
              py={ 1.5 }
              fontSize="10.9px"
              fontWeight={ 500 }
              color={ pillColor }
              whiteSpace="nowrap"
              ml={ 3 }
            >
              { totalReward.dp(5).toFixed() } { config.chain.currency.symbol }
            </Box>
          </Skeleton>
        ) }
      </Flex>
    </Flex>
  );
};

export default LatestBlocksItem;
